import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate } from "@/lib/format";
import BranchFilter from "@/components/BranchFilter";
import DateRangeFilter from "@/components/DateRangeFilter";
import useBranches from "@/hooks/useBranches";
import { Plus, MagnifyingGlass, Trash, ArrowRight, CaretRight, X } from "@phosphor-icons/react";
import PartPicker from "@/components/PartPicker";
import { TestRideButton } from "@/components/GatePassButtons";

/**
 * Job Cards tab under Service. Lifecycle open → parts_issued → billed → closed.
 * Two components in this file:
 *   - `JobCardsTab` — the list + filters (default export).
 *   - `JobCardFormDrawer` — inline drawer for create + full detail view
 *     with issuance-add-line action.
 */
const STATUS_CLS = {
  open: "border-ink text-ink",
  parts_issued: "border-[#E08600] text-[#A35700]",
  billed: "border-[#5f3dc4] text-[#5f3dc4]",
  closed: "border-canvas-line text-ink-subtle",
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function JobCardsTab({ canCreate, canEdit, canDelete }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [branchId, setBranchId] = useState("");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(today); start.setDate(today.getDate() - 29);
    return { from: ymd(start), to: ymd(today) };
  });
  const [drawer, setDrawer] = useState({ open: false, editing: null });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (status) params.status = status;
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/job-cards", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, branchId, dateRange.from, dateRange.to]);

  const kpis = useMemo(() => {
    return rows.reduce((acc, r) => ({
      total: acc.total + 1,
      parts_value: acc.parts_value + (Number(r.parts_total) || 0),
      warranty_value: acc.warranty_value + (Number(r.warranty_total) || 0),
      open: acc.open + (r.status === "open" ? 1 : 0),
      parts_issued: acc.parts_issued + (r.status === "parts_issued" ? 1 : 0),
      billed: acc.billed + (r.status === "billed" ? 1 : 0),
    }), { total: 0, parts_value: 0, warranty_value: 0, open: 0, parts_issued: 0, billed: 0 });
  }, [rows]);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete JC ${row.jc_no}? Any linked spare movements will be reversed.`)) return;
    try {
      await api.delete(`/job-cards/${row.id}`);
      toast.success("Job card deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <BranchFilter value={branchId} onChange={setBranchId} testId="jc-branch-filter" kind="service" />
        <DateRangeFilter value={dateRange} onChange={setDateRange} testIdPrefix="jc-date" />
        <div className="flex items-center border border-canvas-line bg-canvas">
          <MagnifyingGlass size={12} weight="bold" className="mx-2 text-ink-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search JC / vehicle / customer"
            data-testid="jc-search"
            className="px-1 py-2 bg-canvas text-sm focus:outline-none w-64"
          />
        </div>
        <div className="inline-flex border border-canvas-line" data-testid="jc-status-filter">
          {[["", "All"], ["open", "Open"], ["parts_issued", "In Progress"], ["billed", "Billed"], ["closed", "Closed"]].map(([v, l], i) => (
            <button key={v || "all"} type="button" onClick={() => setStatus(v)}
              data-testid={`jc-status-${v || "all"}`}
              className={`px-3 py-2 text-[11px] font-mono uppercase tracking-wider ${i > 0 ? "border-l border-canvas-line" : ""} ${status === v ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}>
              {l}
            </button>
          ))}
        </div>
        {canCreate && (
          <button type="button" onClick={() => setDrawer({ open: true, editing: null })}
            data-testid="new-jc-button"
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-ink/90">
            <Plus size={12} weight="bold" /> New Job Card
          </button>
        )}
      </div>

      <div className="grid grid-cols-6 gap-2" data-testid="jc-kpis">
        <Kpi label="JC Count" value={kpis.total} />
        <Kpi label="Open" value={kpis.open} />
        <Kpi label="In Progress" value={kpis.parts_issued} />
        <Kpi label="Billed" value={kpis.billed} />
        <Kpi label="Parts ₹" value={fmtMoney(kpis.parts_value)} />
        <Kpi label="Warranty ₹" value={fmtMoney(kpis.warranty_value)} />
      </div>

      <div className="border border-canvas-line bg-canvas-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="job-cards-table">
          <thead className="bg-canvas-panel">
            <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">JC #</th>
              <th className="px-3 py-2.5">Vehicle / Customer</th>
              <th className="px-3 py-2.5">Advisor / Tech</th>
              <th className="px-3 py-2.5 text-right">Parts ₹</th>
              <th className="px-3 py-2.5 text-right">Warranty ₹</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Invoice</th>
              <th className="px-3 py-2.5 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">
                No job cards. Create the first one to start tracking parts & warranty issuance.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`jc-row-${r.id}`}
                  className="border-t border-canvas-line hover:bg-canvas-panel/40 cursor-pointer"
                  onClick={() => setDrawer({ open: true, editing: r })}>
                <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-3 py-2.5 font-mono">{r.jc_no}</td>
                <td className="px-3 py-2.5">
                  <div className="font-medium">{r.vehicle_reg_no || "—"}</div>
                  <div className="text-xs text-ink-muted">{r.customer_name || ""}</div>
                </td>
                <td className="px-3 py-2.5 text-xs">
                  <div>{r.advisor || "—"}</div>
                  <div className="text-ink-muted">{r.technician || ""}</div>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold">{fmtMoney(r.parts_total)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(r.warranty_total)}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${STATUS_CLS[r.status] || ""}`}>
                    {r.status.replace("_", " ")}
                  </span>
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-ink-muted">{r.linked_invoice_number || "—"}</td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    {r.status !== "closed" && <TestRideButton jobCard={r} />}
                    <button type="button" data-testid={`open-jc-${r.id}`}
                      onClick={(e) => { e.stopPropagation(); setDrawer({ open: true, editing: r }); }}
                      className="p-1.5 border border-canvas-line hover:border-ink" title="Open">
                      <CaretRight size={12} weight="bold" />
                    </button>
                    {canDelete && (r.status === "open" || r.status === "parts_issued") && (
                      <button type="button" data-testid={`delete-jc-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); onDelete(r); }}
                        className="p-1.5 border border-canvas-line hover:border-destructive text-destructive" title="Delete">
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drawer.open && (
        <JobCardDrawer
          editing={drawer.editing}
          canEdit={canEdit}
          onClose={() => setDrawer({ open: false, editing: null })}
          onSaved={() => { setDrawer({ open: false, editing: null }); load(); }}
        />
      )}
    </>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-canvas-line bg-canvas-card p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className="font-display text-lg font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

// -------- JobCardDrawer -----------------------------------------------------

function JobCardDrawer({ editing, canEdit, onClose, onSaved }) {
  const { branches: serviceBranches } = useBranches({ activeOnly: true, kind: "service" });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(editing);
  const [advisors, setAdvisors] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    branch_id: serviceBranches[0]?.id || "",
    jc_no: "",
    customer_name: "", customer_phone: "",
    vehicle_reg_no: "",
    advisor: "", technician: "",
    complaint: "", notes: "",
  });

  // Load advisors/technicians scoped to the picked (or edited) branch —
  // matches the same pattern as the Service Invoice drawer.
  useEffect(() => {
    const bid = form.branch_id || detail?.branch_id;
    (async () => {
      try {
        const params = { active_only: true };
        if (bid) params.branch_id = bid;
        const [ad, te] = await Promise.all([
          api.get("/service-staff", { params: { ...params, role: "advisor" } }),
          api.get("/service-staff", { params: { ...params, role: "technician" } }),
        ]);
        setAdvisors(ad.data || []);
        setTechnicians(te.data || []);
      } catch (err) {
        console.warn("staff load failed", err);
      }
    })();
  }, [form.branch_id, detail?.branch_id]);
  const [showAddMovement, setShowAddMovement] = useState(false);
  const [movementType, setMovementType] = useState("jc_issuance");
  const [warrantyTag, setWarrantyTag] = useState("");
  const [movementLines, setMovementLines] = useState([{ part_code: "", description: "", qty: 1, unit_price: 0, tax_percent: 0 }]);
  const [invoiceLinkOpen, setInvoiceLinkOpen] = useState(false);
  const [invoiceRef, setInvoiceRef] = useState({ id: "", number: "" });

  const isEdit = !!editing;

  // Load full detail when editing (to fetch movements[]).
  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/job-cards/${editing.id}`);
        setDetail(data);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [editing]);

  useEffect(() => {
    if (!editing && serviceBranches.length === 1 && !form.branch_id) {
      setForm((f) => ({ ...f, branch_id: serviceBranches[0].id }));
    }
  }, [serviceBranches, editing, form.branch_id]);

  const onSaveNew = async () => {
    if (!form.branch_id) { toast.error("Pick a branch."); return; }
    if (!form.customer_name.trim()) { toast.error("Customer name is required."); return; }
    setSaving(true);
    try {
      await api.post("/job-cards", form);
      toast.success("Job card created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const addLine = () => setMovementLines((ls) => [...ls, { part_code: "", description: "", qty: 1, unit_price: 0, tax_percent: 0 }]);
  const updateLine = (i, k, v) => setMovementLines((ls) => ls.map((l, ix) => ix === i ? { ...l, [k]: v } : l));
  const removeLine = (i) => setMovementLines((ls) => ls.filter((_, ix) => ix !== i));

  // Look up a part from the master when the operator types a part_code + tab.
  const lookupPart = async (i, code) => {
    if (!code.trim()) return;
    try {
      const { data } = await api.get("/spare-parts", { params: { search: code.trim() } });
      const hit = (data || []).find((p) => p.part_code === code.trim());
      if (hit) {
        updateLine(i, "description", hit.description || "");
        updateLine(i, "unit_price", hit.unit_price || 0);
        updateLine(i, "tax_percent", hit.tax_percent || 0);
      }
    } catch (err) {
      // Non-fatal — operator can type the description manually.
    }
  };

  const saveMovement = async () => {
    const nonEmpty = movementLines.filter((l) => l.part_code && Number(l.qty) > 0);
    if (nonEmpty.length === 0) { toast.error("Add at least one part with qty > 0."); return; }
    if (movementType === "jc_warranty" && !warrantyTag.trim()) {
      toast.error("Warranty tag # (from DMS) is required.");
      return;
    }
    try {
      await api.post("/spare-movements", {
        movement_type: movementType,
        date: new Date().toISOString().slice(0, 10),
        branch_id: detail.branch_id,
        jc_id: detail.id,
        warranty_tag_no: movementType === "jc_warranty" ? warrantyTag.trim() : "",
        lines: nonEmpty,
      });
      toast.success(`${movementType === "jc_warranty" ? "Warranty" : "Parts"} issued`);
      setShowAddMovement(false);
      setWarrantyTag("");
      setMovementLines([{ part_code: "", description: "", qty: 1, unit_price: 0, tax_percent: 0 }]);
      const { data } = await api.get(`/job-cards/${detail.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markBilled = async () => {
    if (!invoiceRef.id || !invoiceRef.number.trim()) { toast.error("Enter both the invoice ID and number."); return; }
    try {
      await api.patch(`/job-cards/${detail.id}`, {
        status: "billed",
        linked_invoice_id: invoiceRef.id,
        linked_invoice_number: invoiceRef.number.trim(),
      });
      toast.success("Job card marked billed");
      setInvoiceLinkOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markClosed = async () => {
    if (!window.confirm("Close this job card? This is a soft-lock — you can still view it.")) return;
    try {
      await api.patch(`/job-cards/${detail.id}`, { status: "closed" });
      toast.success("Job card closed");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteMovement = async (mvId) => {
    if (!window.confirm("Delete this movement? Running totals will reverse.")) return;
    try {
      await api.delete(`/spare-movements/${mvId}`);
      toast.success("Movement removed");
      const { data } = await api.get(`/job-cards/${detail.id}`);
      setDetail(data);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-[720px] bg-canvas-card border-l-2 border-ink overflow-y-auto"
           data-testid="jc-drawer">
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between sticky top-0 bg-canvas-card z-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              {isEdit ? `${detail?.jc_no || ""}` : "New Job Card"}
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">
              {isEdit ? (detail?.customer_name || "—") : "Create Job Card"}
              {isEdit && detail?.status && (
                <span className={`ml-2 inline-block px-2 py-0.5 border text-[10px] font-mono uppercase align-middle ${STATUS_CLS[detail.status] || ""}`}>
                  {detail.status.replace("_", " ")}
                </span>
              )}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!isEdit && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Date" type="date" value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} />
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Branch *</label>
                  <select value={form.branch_id} onChange={(e) => setForm((f) => ({ ...f, branch_id: e.target.value }))}
                    data-testid="jc-form-branch"
                    className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink">
                    <option value="">— select service branch —</option>
                    {serviceBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <Field label="JC # (auto if blank)" value={form.jc_no} onChange={(v) => setForm((f) => ({ ...f, jc_no: v }))} mono />
                <Field label="Vehicle Reg #" value={form.vehicle_reg_no} onChange={(v) => setForm((f) => ({ ...f, vehicle_reg_no: v }))} mono />
                <Field label="Customer *" value={form.customer_name} onChange={(v) => setForm((f) => ({ ...f, customer_name: v }))} />
                <Field label="Phone" value={form.customer_phone} onChange={(v) => setForm((f) => ({ ...f, customer_phone: v }))} mono />
                <StaffPicker
                  label="Advisor"
                  value={form.advisor}
                  onChange={(v) => setForm((f) => ({ ...f, advisor: v }))}
                  options={advisors}
                  testId="jc-advisor"
                />
                <StaffPicker
                  label="Technician"
                  value={form.technician}
                  onChange={(v) => setForm((f) => ({ ...f, technician: v }))}
                  options={technicians}
                  testId="jc-technician"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Complaint / Observation</label>
                <textarea value={form.complaint} onChange={(e) => setForm((f) => ({ ...f, complaint: e.target.value }))}
                  rows={2} className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink" />
              </div>
              <div className="pt-3 border-t border-canvas-line flex justify-end gap-2">
                <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">Cancel</button>
                <button type="button" onClick={onSaveNew} disabled={saving}
                  data-testid="save-jc"
                  className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-ink/90 disabled:opacity-60">
                  {saving ? "Saving…" : "Create Job Card"}
                </button>
              </div>
            </>
          )}

          {isEdit && detail && (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <MiniField label="Vehicle" value={detail.vehicle_reg_no} />
                <MiniField label="Customer" value={detail.customer_name} />
                <MiniField label="Phone" value={detail.customer_phone} />
                <MiniField label="Date" value={fmtDate(detail.date)} />
                <MiniField label="Advisor" value={detail.advisor} />
                <MiniField label="Technician" value={detail.technician} />
                <MiniField label="Complaint" value={detail.complaint} colspan={2} />
              </div>

              <div className="border border-canvas-line">
                <div className="px-3 py-2 bg-canvas-panel flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                    Spare Movements ({detail.movements?.length || 0})
                  </div>
                  {canEdit && (detail.status === "open" || detail.status === "parts_issued") && (
                    <button type="button" onClick={() => setShowAddMovement((s) => !s)}
                      data-testid="add-movement-toggle"
                      className="inline-flex items-center gap-1 px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase hover:border-ink">
                      <Plus size={10} weight="bold" /> Issue parts
                    </button>
                  )}
                </div>
                <table className="w-full text-xs" data-testid="jc-movements-table">
                  <thead className="bg-canvas-panel/50">
                    <tr className="text-left font-mono text-[9px] uppercase tracking-wider text-ink-muted">
                      <th className="px-2 py-1.5">Date</th>
                      <th className="px-2 py-1.5">Type</th>
                      <th className="px-2 py-1.5">Part</th>
                      <th className="px-2 py-1.5 text-right">Qty</th>
                      <th className="px-2 py-1.5 text-right">₹</th>
                      <th className="px-2 py-1.5 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.movements || []).flatMap((m) =>
                      m.lines.map((l, li) => (
                        <tr key={`${m.id}-${li}`} className="border-t border-canvas-line">
                          {li === 0 && (
                            <>
                              <td className="px-2 py-1.5" rowSpan={m.lines.length}>{fmtDate(m.date)}</td>
                              <td className="px-2 py-1.5" rowSpan={m.lines.length}>
                                <span className={`inline-block px-1.5 py-0.5 border text-[9px] font-mono uppercase ${
                                  m.movement_type === "jc_warranty" ? "border-[#00713a] text-[#00713a]" : "border-ink text-ink"
                                }`}>
                                  {m.movement_type === "jc_warranty" ? "warranty" : "parts"}
                                </span>
                                {m.movement_type === "jc_warranty" && m.warranty_tag_no && (
                                  <div className="mt-1 font-mono text-[9px] text-ink-muted" data-testid={`warranty-tag-${m.id}`}>
                                    DMS: <b className="text-[#00713a]">{m.warranty_tag_no}</b>
                                  </div>
                                )}
                              </td>
                            </>
                          )}
                          <td className="px-2 py-1.5">
                            <div className="font-mono">{l.part_code}</div>
                            <div className="text-[10px] text-ink-muted">{l.description}</div>
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{l.qty}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney(l.total)}</td>
                          {li === 0 && (
                            <td className="px-2 py-1.5" rowSpan={m.lines.length}>
                              {canEdit && detail.status !== "billed" && detail.status !== "closed" && (
                                <button type="button" onClick={() => deleteMovement(m.id)}
                                  className="p-0.5 text-destructive hover:opacity-70" title="Delete movement">
                                  <Trash size={10} weight="bold" />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                    {(detail.movements || []).length === 0 && (
                      <tr><td colSpan={6} className="px-2 py-4 text-center text-ink-subtle font-mono text-[10px]">
                        No parts issued yet.
                      </td></tr>
                    )}
                  </tbody>
                </table>
                <div className="flex justify-end gap-6 px-3 py-2 bg-canvas-panel/40 border-t border-canvas-line text-xs font-mono">
                  <div>Parts: <b className="tabular-nums">{fmtMoney(detail.parts_total)}</b></div>
                  <div>Warranty: <b className="tabular-nums">{fmtMoney(detail.warranty_total)}</b></div>
                </div>
              </div>

              {showAddMovement && (
                <div className="border border-canvas-line p-3 space-y-2 bg-canvas-panel/20" data-testid="add-movement-panel">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Type:</span>
                    {[["jc_issuance", "Parts"], ["jc_warranty", "Warranty"]].map(([v, l]) => (
                      <label key={v} className="inline-flex items-center gap-1 text-xs cursor-pointer">
                        <input type="radio" checked={movementType === v} onChange={() => setMovementType(v)} className="accent-ink" />
                        {l}
                      </label>
                    ))}
                  </div>
                  {movementType === "jc_warranty" && (
                    <div className="flex items-center gap-2" data-testid="warranty-tag-row">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted whitespace-nowrap">Warranty Tag # <span className="text-destructive">*</span></span>
                      <input
                        type="text"
                        value={warrantyTag}
                        onChange={(e) => setWarrantyTag(e.target.value)}
                        placeholder="From DMS system"
                        data-testid="warranty-tag-input"
                        className="flex-1 px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono focus:outline-none focus:border-ink" />
                    </div>
                  )}
                  <table className="w-full text-xs">
                    <thead className="bg-canvas-panel/50">
                      <tr className="text-left font-mono text-[9px] uppercase tracking-wider text-ink-muted">
                        <th className="px-2 py-1 w-24">Part Code</th>
                        <th className="px-2 py-1">Description</th>
                        <th className="px-2 py-1 w-14 text-right">Qty</th>
                        <th className="px-2 py-1 w-16 text-right">Price</th>
                        <th className="px-2 py-1 w-12 text-right">Tax%</th>
                        <th className="px-2 py-1 w-6"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {movementLines.map((l, i) => (
                        <tr key={i} className="border-t border-canvas-line">
                          <td className="px-1 py-1">
                            <PartPicker
                              value={l.part_code}
                              onTypedValue={(v) => updateLine(i, "part_code", v)}
                              onSelect={(p) => {
                                // Autofill the whole row from the master hit.
                                setMovementLines((ls) => ls.map((ln, ix) => ix === i ? {
                                  ...ln,
                                  part_code: p.part_code,
                                  description: p.description || "",
                                  unit_price: p.unit_price || 0,
                                  tax_percent: p.tax_percent || 0,
                                } : ln));
                              }}
                              testId={`add-line-part-${i}`}
                            />
                          </td>
                          <td className="px-1 py-1">
                            <input value={l.description} onChange={(e) => updateLine(i, "description", e.target.value)}
                              className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink" />
                          </td>
                          <td className="px-1 py-1"><input type="number" value={l.qty} onChange={(e) => updateLine(i, "qty", e.target.value)}
                            className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-xs text-right tabular-nums focus:outline-none focus:border-ink" /></td>
                          <td className="px-1 py-1"><input type="number" value={l.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)}
                            className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-xs text-right tabular-nums focus:outline-none focus:border-ink" /></td>
                          <td className="px-1 py-1"><input type="number" value={l.tax_percent} onChange={(e) => updateLine(i, "tax_percent", e.target.value)}
                            className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-xs text-right tabular-nums focus:outline-none focus:border-ink" /></td>
                          <td className="px-1 py-1">
                            <button type="button" onClick={() => removeLine(i)} className="p-0.5 text-destructive"><Trash size={10} weight="bold" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex justify-between items-center">
                    <button type="button" onClick={addLine} className="text-[11px] font-mono uppercase tracking-wider text-ink-muted hover:text-ink">
                      + Add line
                    </button>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setShowAddMovement(false)} className="px-3 py-1.5 border border-canvas-line text-xs">Cancel</button>
                      <button type="button" onClick={saveMovement}
                        data-testid="save-movement"
                        className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-ink/90">
                        Save movement
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-canvas-line space-y-2">
                {detail.status !== "billed" && detail.status !== "closed" && canEdit && (
                  <button type="button" onClick={() => setInvoiceLinkOpen(true)}
                    data-testid="mark-billed"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-[#5f3dc4] text-white text-sm font-semibold hover:bg-[#4c30a0]">
                    Mark as Billed → Link DMS Invoice <ArrowRight size={12} weight="bold" />
                  </button>
                )}
                {detail.status === "billed" && canEdit && (
                  <button type="button" onClick={markClosed}
                    data-testid="mark-closed"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-canvas-line text-sm hover:bg-canvas-panel">
                    Close job card
                  </button>
                )}
                {detail.linked_invoice_number && (
                  <div className="text-xs font-mono text-ink-muted text-center">
                    Linked invoice: <span className="text-ink">{detail.linked_invoice_number}</span>
                  </div>
                )}
              </div>

              {invoiceLinkOpen && (
                <div className="border-2 border-[#5f3dc4] p-3 space-y-2 bg-[#5f3dc4]/5" data-testid="link-invoice-panel">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Link DMS invoice</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Invoice ID" value={invoiceRef.id} onChange={(v) => setInvoiceRef((r) => ({ ...r, id: v }))} testId="link-inv-id" mono />
                    <Field label="Invoice #" value={invoiceRef.number} onChange={(v) => setInvoiceRef((r) => ({ ...r, number: v }))} testId="link-inv-num" mono />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setInvoiceLinkOpen(false)} className="px-3 py-1.5 border border-canvas-line text-xs">Cancel</button>
                    <button type="button" onClick={markBilled}
                      data-testid="confirm-billed"
                      className="px-3 py-1.5 bg-[#5f3dc4] text-white text-xs font-semibold hover:bg-[#4c30a0]">
                      Confirm billed
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", mono, testId }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink ${mono ? "font-mono" : ""}`} />
    </div>
  );
}
function MiniField({ label, value, colspan }) {
  return (
    <div className={colspan === 2 ? "col-span-2" : ""}>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-subtle">{label}</div>
      <div className="text-sm">{value || <span className="text-ink-subtle">—</span>}</div>
    </div>
  );
}

/**
 * Advisor / Technician picker — mirrors ServiceInvoiceDrawer.StaffPicker.
 * Preserves legacy free-text names via a disabled "(legacy)" option so
 * old job cards remain editable when the master doesn't yet contain the
 * originally-typed name.
 */
function StaffPicker({ label, value, onChange, options, testId }) {
  const known = new Set((options || []).map((o) => o.name));
  const isLegacy = value && !known.has(value);
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">{label}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
      >
        <option value="">— none —</option>
        {isLegacy && <option value={value}>{value} (legacy)</option>}
        {(options || []).map((o) => (
          <option key={o.id} value={o.name}>
            {o.name}{o.code ? ` · ${o.code}` : ""}
          </option>
        ))}
      </select>
      {(options || []).length === 0 && (
        <div className="mt-1 font-mono text-[10px] text-ink-subtle">
          No entries yet — add via Service Staff master.
        </div>
      )}
    </div>
  );
}
